import { Phone, Mail, User, Users } from "lucide-react";
import { contactPhones, contactEmails, contactName } from "@/lib/shariti";

const telHref = (t) => `tel:${String(t).replace(/[^0-9+]/g, "")}`;

/** Une puce téléphone cliquable (click-to-call). */
function PhoneChip({ value, idx, compact = false }) {
  return (
    <a
      href={telHref(value)}
      onClick={(e) => e.stopPropagation()}
      data-testid={`contact-tel-${idx}`}
      title={`Appeler ${value}`}
      className={`inline-flex w-fit max-w-full items-center gap-1 whitespace-nowrap rounded-full border border-[rgba(31,157,85,0.3)] bg-[rgba(31,157,85,0.10)] font-semibold text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(31,157,85,0.4)] ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"}`}
    >
      <Phone className="h-3 w-3 shrink-0" />
      <span className="tabular-nums">{value}</span>
    </a>
  );
}

/**
 * Une puce email cliquable (mailto).
 * ``wrap`` : affiche l'email en entier (retour à la ligne dans la puce), sinon
 * tronque proprement avec info-bulle.
 */
function EmailChip({ value, idx, wrap = false, compact = false }) {
  return (
    <a
      href={`mailto:${value}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`contact-mail-${idx}`}
      title={value}
      className={`inline-flex max-w-full min-w-0 gap-1 rounded-full border border-[rgba(47,111,237,0.28)] bg-[rgba(47,111,237,0.08)] font-semibold text-[#1D4ED8] transition-colors hover:bg-[rgba(47,111,237,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(47,111,237,0.4)] ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"} ${wrap ? "items-start" : "items-center"}`}
    >
      <Mail className={`h-3 w-3 shrink-0 ${wrap ? "mt-[2px]" : ""}`} />
      <span className={wrap ? "min-w-0 break-all leading-snug" : "truncate"}>{value}</span>
    </a>
  );
}

/**
 * Bloc compact d'un contact : nom + fonction + puces tél/email cliquables.
 * ``stack`` (tableaux à largeur contrainte) : empile les puces verticalement et
 * affiche nom / fonction / email EN ENTIER (retour à la ligne, aucune troncature).
 * ``compact`` : réduit les tailles de police (tableaux denses).
 */
export function ContactChips({ contact, showName = true, stack = false, compact = false }) {
  const name = contactName(contact);
  const fonction = contact?.fonction;
  const phones = contactPhones(contact);
  const emails = contactEmails(contact);
  if (!name && phones.length === 0 && emails.length === 0) {
    return <span className="text-xs text-muted-ink">—</span>;
  }
  const nameCls = stack
    ? `block break-words leading-tight ${compact ? "text-[11px]" : "text-[13px]"}`
    : "truncate text-sm";
  const fonctionCls = stack
    ? `block break-words leading-tight ${compact ? "text-[9px]" : "text-[10px]"}`
    : "truncate text-[11px]";
  return (
    <div className="min-w-0">
      {showName && (name || fonction) && (
        <div className={stack ? "space-y-0.5" : "flex flex-wrap items-baseline gap-x-1.5"}>
          {name && (
            <span className={`font-semibold text-ink ${nameCls}`}>{name}</span>
          )}
          {fonction && (
            <span className={`text-muted-ink ${fonctionCls}`}>{stack ? fonction : `· ${fonction}`}</span>
          )}
        </div>
      )}
      {(phones.length > 0 || emails.length > 0) && (
        <div className={`mt-1 flex min-w-0 gap-1.5 ${stack ? "flex-col items-start" : "flex-wrap"}`}>
          {phones.map((p, i) => <PhoneChip key={`p${i}`} value={p} idx={i} compact={compact} />)}
          {emails.map((em, i) => <EmailChip key={`e${i}`} value={em} idx={i} wrap={stack} compact={compact} />)}
        </div>
      )}
    </div>
  );
}

/** Tous les contacts d'un spot (principal + secondaires), mise en page responsive. */
export function AllContacts({ spot, emptyHint = "Aucun contact renseigné." }) {
  const principal = spot?.contact_principal;
  const secondaires = (spot?.contacts_secondaires || []).filter(
    (c) => contactName(c) || contactPhones(c).length || contactEmails(c).length
  );
  const hasPrincipal =
    principal && (contactName(principal) || contactPhones(principal).length || contactEmails(principal).length);

  if (!hasPrincipal && secondaires.length === 0) {
    return <div className="text-sm text-muted-ink" data-testid="contacts-empty">{emptyHint}</div>;
  }
  return (
    <div className="space-y-2.5" data-testid="all-contacts">
      {hasPrincipal && (
        <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
          <div className="mb-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
            <User className="h-3 w-3" /> Contact principal
          </div>
          <ContactChips contact={principal} />
        </div>
      )}
      {secondaires.map((c, i) => (
        <div key={i} className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
          <div className="mb-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
            <Users className="h-3 w-3" /> {c.fonction || `Contact ${i + 2}`}
          </div>
          <ContactChips contact={c} showName={!c.fonction || !!contactName(c)} />
        </div>
      ))}
    </div>
  );
}
